import { SOURCE_GAME_INPUT_PROMPT } from "./kinds/shared/prompt";
import type { GameBlueprint } from "./blueprint";
import { parseCarcadeGameSpec, type CarcadeGameSpec } from "./game-spec";
import type { ModelJsonRequester } from "./generation-pipeline";
import { compactPlaytestEvidence, type PlaytestEvidence } from "./playtest-evidence";

export type CritiqueSeverity = "blocker" | "major" | "minor";

export interface CritiqueProblem {
  readonly severity: CritiqueSeverity;
  readonly criterion: string;
  readonly evidence: string;
  readonly requiredChange: string;
}

export interface PlaytestCritique {
  readonly version: 1;
  readonly passed: boolean;
  readonly score: number;
  readonly summary: string;
  readonly problems: readonly CritiqueProblem[];
}

function record(value: unknown, path: string): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new Error(`${path} must be an object`);
  return value as Record<string, unknown>;
}

function exactKeys(value: Record<string, unknown>, keys: readonly string[], path: string): void {
  for (const key of Object.keys(value)) if (!keys.includes(key)) throw new Error(`${path}.${key} is not allowed`);
  for (const key of keys) if (!(key in value)) throw new Error(`${path}.${key} is required`);
}

function text(value: unknown, path: string, max: number): string {
  if (typeof value !== "string" || !value.trim() || value.length > max) throw new Error(`${path} must be non-empty text <= ${max} characters`);
  return value;
}

export function parsePlaytestCritique(value: unknown): PlaytestCritique {
  const root = record(value, "critique");
  exactKeys(root, ["version", "passed", "score", "summary", "problems"], "critique");
  if (root.version !== 1) throw new Error("critique.version must be 1");
  if (typeof root.passed !== "boolean") throw new Error("critique.passed must be boolean");
  if (typeof root.score !== "number" || !Number.isInteger(root.score) || root.score < 0 || root.score > 100) throw new Error("critique.score must be an integer 0-100");
  if (!Array.isArray(root.problems) || root.problems.length > 12) throw new Error("critique.problems must be an array of at most 12 entries");
  const problems = root.problems.map((entry, index) => {
    const problem = record(entry, `critique.problems[${index}]`);
    exactKeys(problem, ["severity", "criterion", "evidence", "requiredChange"], `critique.problems[${index}]`);
    if (problem.severity !== "blocker" && problem.severity !== "major" && problem.severity !== "minor") throw new Error(`critique.problems[${index}].severity is invalid`);
    return {
      severity: problem.severity,
      criterion: text(problem.criterion, `critique.problems[${index}].criterion`, 320),
      evidence: text(problem.evidence, `critique.problems[${index}].evidence`, 500),
      requiredChange: text(problem.requiredChange, `critique.problems[${index}].requiredChange`, 700),
    } as CritiqueProblem;
  });
  if (root.passed && problems.some((problem) => problem.severity !== "minor")) throw new Error("a passing critique cannot contain blocker or major problems");
  return { version: 1, passed: root.passed, score: root.score, summary: text(root.summary, "critique.summary", 1_200), problems };
}

function sourceGame(spec: CarcadeGameSpec): asserts spec is CarcadeGameSpec & { readonly kind: "source-game"; readonly blueprint: GameBlueprint; readonly source: { readonly markup: string; readonly styles: string; readonly script: string } } {
  if (spec.kind !== "source-game") throw new Error("autonomous critique requires a source-game");
}

function sameBlueprint(left: GameBlueprint, right: GameBlueprint): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

export function buildPlaytestCriticPrompt(brief: string, spec: CarcadeGameSpec, evidence: PlaytestEvidence): string {
  sourceGame(spec);
  const compact = compactPlaytestEvidence(evidence);
  return [
    "You are CARCADE's adversarial game director, senior QA lead, and visual critic.",
    "Judge the ACTUAL generated game, not the intent. Reject generously. A technically running game that does not obviously embody the requested fantasy is a failure.",
    "Use the supplied screenshot when present. Check whether requested actors and objects are visually recognizable, whether the composition reads as a game rather than programmer placeholders, and whether the UI/scene supports the requested fantasy.",
    "Use runtime evidence to check that input caused meaningful state changes, no runtime error occurred, and the game exposes enough state to demonstrate real mechanics.",
    "For continuous movement requested by the blueprint, reject evidence consisting only of actions sent, a changing timer, animation without player displacement, or movement labels contradicted by positions. Require held movement across multiple state samples with corresponding position/velocity changes. Require independent jump/release evidence only when the approved blueprint includes jumping; when it also requires movement, prove their overlap. Do not invent a jump requirement for racing, paddle, or other games without that mechanic. Synthetic input is desktop software evidence, not physical Tesla acceptance.",
    "Check every blueprint acceptance criterion. If evidence cannot support a criterion, treat that as a problem rather than assuming it works.",
    "Dots, circles, generic targets, emoji stand-ins, labels describing nonexistent systems, menus without gameplay, and cosmetic reskins are major failures unless the brief specifically requests them.",
    "Return JSON only with exactly this shape:",
    '{"version":1,"passed":boolean,"score":integer 0-100,"summary":"direct verdict","problems":[{"severity":"blocker|major|minor","criterion":"what failed","evidence":"what you actually observed","requiredChange":"specific source-level repair"}]}',
    "Passing requires score >= 85, no blocker/major problems, recognizable requested game identity, meaningful response to exploratory input, and no runtime errors.",
    `User brief: ${brief}`,
    `Blueprint: ${JSON.stringify(spec.blueprint)}`,
    `Playtest evidence: ${JSON.stringify(compact)}`,
  ].join("\n");
}

export function buildEvidenceRevisionPrompt(brief: string, spec: CarcadeGameSpec, evidence: PlaytestEvidence, critique: PlaytestCritique): string {
  sourceGame(spec);
  return [
    "You are the principal CARCADE game engineer revising a game after an adversarial automated playtest.",
    "Return a FULL corrected source-game JSON object. Preserve the title and blueprint EXACTLY. Rewrite markup/styles/script as much as necessary.",
    "Do not delete a requested mechanic to make validation easier. Fix the implementation. Do not replace concrete actors with generic dots/circles/emoji placeholders.",
    "Keep the same sandbox contract: no network/imports/navigation/parent access/cookies/direct persistent storage/workers/eval. Use $CARCADE for ready/state/event/pause integration.",
    SOURCE_GAME_INPUT_PROMPT,
    "The revised game must report rich serializable state through $CARCADE.state whenever meaningful gameplay changes occur so the next playtest can prove the systems are alive.",
    `User brief: ${brief}`,
    `Current game: ${JSON.stringify(spec)}`,
    `Playtest evidence: ${JSON.stringify(compactPlaytestEvidence(evidence))}`,
    `Critique: ${JSON.stringify(critique)}`,
  ].join("\n");
}

export async function critiqueSourceGameWithModel(brief: string, spec: CarcadeGameSpec, evidence: PlaytestEvidence, requestJson: ModelJsonRequester): Promise<PlaytestCritique> {
  const result = await requestJson({
    phase: "critic",
    prompt: buildPlaytestCriticPrompt(brief, spec, evidence),
    temperature: 0.15,
    ...(evidence.visual?.dataUrl ? { images: [evidence.visual.dataUrl] } : {}),
  });
  return parsePlaytestCritique(result.candidate);
}

export async function reviseSourceGameFromEvidence(brief: string, spec: CarcadeGameSpec, evidence: PlaytestEvidence, critique: PlaytestCritique, requestJson: ModelJsonRequester): Promise<CarcadeGameSpec> {
  sourceGame(spec);
  const result = await requestJson({ phase: "revision", prompt: buildEvidenceRevisionPrompt(brief, spec, evidence, critique), temperature: 0.35 });
  const revised = parseCarcadeGameSpec(result.candidate);
  if (revised.kind !== "source-game") throw new Error("revision must return a source-game");
  if (revised.title !== spec.title || !sameBlueprint(revised.blueprint, spec.blueprint)) throw new Error("revision changed the approved title or blueprint");
  return revised;
}
